/** @file Changes an IK constraint: chain, target, order, mix, softness and flags (rules re-checked). */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import type { IkConstraintDoc } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { assertIkChain } from './assertIkChain';
import { editWithoutIkCycle } from './editWithoutIkCycle';
import { resolveChain } from './resolveChain';
import { warnIkChain } from './warnIkChain';

const ref = z.string().min(1);
const valueSchema = z
  .object({
    bones: z.union([z.tuple([ref]), z.tuple([ref, ref])]).exactOptional(),
    target: ref.exactOptional(),
    order: z.number().int().nonnegative().exactOptional(),
    mix: prim.unit.exactOptional(),
    softness: prim.finite.nonnegative().exactOptional(),
    bendPositive: z.boolean().exactOptional(),
    compress: z.boolean().exactOptional(),
    stretch: z.boolean().exactOptional(),
    uniform: z.boolean().exactOptional(),
  })
  .strict();

/** The requested fields with bone references resolved to ids. */
function resolved(value: z.infer<typeof valueSchema>, ctx: CommandContext): Partial<IkConstraintDoc> {
  const { bones, target, ...flags } = value;
  return { ...flags, ...(bones === undefined ? {} : { bones: resolveChain(ctx, bones) }), ...(target === undefined ? {} : { target: ctx.resolve('bone', target) }) };
}

/**
 * ik.set: merges the given fields (a value equal to the constraint is a no-op), re-checks §6.5 and the §6.6 loop rule
 * (CYCLE at `value.target`, else `value.bones`), reports the INFOs.
 */
export const command = defineCommand({
  name: 'ik.set',
  description:
    'Change an IK constraint: value = any of bones ([parent] or [parent, child]), target, order, mix 0..1, softness >= 0, bendPositive, compress, stretch, uniform; omitted fields keep their value.',
  input: z.object({ target: ref, value: valueSchema }).strict(),
  apply(draft, input, ctx) {
    const id = ctx.resolve('ik', input.target);
    const ik = draft.constraints[id] ?? throwError('NOT_FOUND', { kind: 'ik', ref: id });
    const next = resolved(input.value, ctx);
    if (Object.entries(next).every(([key, v]) => JSON.stringify(v) === JSON.stringify(ik[key as keyof IkConstraintDoc]))) return;
    editWithoutIkCycle(draft, input.value.target === undefined ? 'value.bones' : 'value.target', () => {
      Object.assign(ik, next);
      assertIkChain(draft, { ik: ik.name, bones: ik.bones, target: ik.target, at: 'value.' });
    });
    warnIkChain(draft, ctx, { bones: ik.bones, stretch: ik.stretch, at: 'value.' });
    draft.meta.updatedAt = ctx.now;
  },
});
